import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, FileDown, LayoutDashboard, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PaywallButton } from "@/components/ui/PaywallButton";
import { CyFunLevelBadge } from "@/components/risk/CyFunLevelBadge";
import { formatScore } from "@/lib/utils";
import type { CyFunLevel } from "@/types/risk-analysis";
import type { ReprezentantEntitate } from "@/types/risk-wizard";
import type { ContactData } from "@/types/contact";

type GenerateReportStepProps = {
  companyName: string;
  globalScore: number;
  globalCyFunLevel: CyFunLevel;
  totalModifications: number;
  analyzedSectorsCount: number;
  entityContactData?: ContactData | null;
  isPdfGenerating?: boolean;
  hasInsufficientPdfCredits?: boolean;
  onDownloadPdf: (reprezentant: ReprezentantEntitate) => void;
};

export function safeReprezentantValue(value: string | null): string {
  return value ?? "";
}

export function GenerateReportStep({
  companyName,
  globalScore,
  globalCyFunLevel,
  totalModifications,
  analyzedSectorsCount,
  entityContactData,
  isPdfGenerating,
  hasInsufficientPdfCredits,
  onDownloadPdf,
}: GenerateReportStepProps) {
  const navigate = useNavigate();
  const legal = entityContactData?.reprezentantLegal;
  const [reprezentant, setReprezentant] = useState<ReprezentantEntitate>({
    prenume: legal?.prenume || null,
    nume: legal?.nume || null,
    functia: legal?.functie || null,
  });

  const handleUpdateField = useCallback(
    (field: keyof ReprezentantEntitate, value: string) => {
      setReprezentant((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  return (
    <div className="space-y-4 md:space-y-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-base md:text-lg">
            Generare Raport
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 md:space-y-4 pt-0 md:pt-0">
          <p className="text-sm md:text-base">
            Evaluarea nivelului de risc pentru <strong>{companyName}</strong>{" "}
            este completă.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2 md:p-4 text-xs md:text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              {analyzedSectorsCount} sectoare analizate
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              {totalModifications} modificări efectuate
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Scor maxim: {formatScore(globalScore)}
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Nivel CyFun: <CyFunLevelBadge level={globalCyFunLevel} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base md:text-lg">
            Reprezentare Entitate
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 md:space-y-4 pt-0 md:pt-0">
          <p className="text-sm text-muted-foreground">Reprezentant legal</p>
          <div className="grid gap-3 md:gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label className="text-xs md:text-sm" htmlFor="prenume">
                Prenume
              </Label>
              <Input
                id="prenume"
                value={safeReprezentantValue(reprezentant.prenume)}
                onChange={(e) => handleUpdateField("prenume", e.target.value)}
                placeholder="Prenume"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs md:text-sm" htmlFor="nume">
                Nume
              </Label>
              <Input
                id="nume"
                value={safeReprezentantValue(reprezentant.nume)}
                onChange={(e) => handleUpdateField("nume", e.target.value)}
                placeholder="Nume"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs md:text-sm" htmlFor="functia">
                Funcția
              </Label>
              <Input
                id="functia"
                value={safeReprezentantValue(reprezentant.functia)}
                onChange={(e) => handleUpdateField("functia", e.target.value)}
                placeholder="Director General"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base md:text-lg">
            Descarcă Raportul
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 md:space-y-4 pt-0 md:pt-0">
          <p className="text-xs md:text-sm">
            Raportul evaluării nivelului de risc (în format PDF), asumat și
            semnat electronic de către reprezentantul entității, va fi transmis
            la DNSC.
          </p>
          <div className="flex justify-center">
            <PaywallButton
              locked={!isPdfGenerating && !!hasInsufficientPdfCredits}
              leftIcon={
                isPdfGenerating ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <FileDown className="h-5 w-5 mr-2" />
                )
              }
              size="lg"
              onClick={() => onDownloadPdf(reprezentant)}
              disabled={isPdfGenerating}
            >
              {isPdfGenerating ? "Se generează..." : "Descarcă PDF"}
            </PaywallButton>
          </div>
          <p className="text-xs text-muted-foreground text-center">
            După descărcare, semnați documentul și transmiteți-l la DNSC conform
            Ordinului Directorului DNSC nr. 1/2025.
          </p>
        </CardContent>
      </Card>

      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="space-y-3 md:space-y-4 p-5">
          <h3 className="font-semibold text-slate-800 text-sm md:text-base">
            Pasul următor: Evaluare Maturitate
          </h3>
          <p className="text-xs md:text-sm text-slate-500">
            Acum că aveți scorul de risc, puteți începe evaluarea de maturitate
            cibernetică pentru a vă conforma cerințelor NIS2.
          </p>
          <div className="flex justify-center">
            <Button
              onClick={() => navigate("/dashboard")}
              size="lg"
              className="gap-2"
            >
              <LayoutDashboard className="h-5 w-5" />
              Către Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
